/**
 * ReadingProgressBar Component
 *
 * A reading progress indicator that shows how much of an article has been read.
 * Progress is tracked from the bottom of the viewport, which shows higher progress
 * percentages earlier - this motivates readers by making them feel "almost done"
 * sooner, encouraging them to finish the article.
 *
 * Design specs (from Figma):
 * - Height: 4px (h-1)
 * - Background: neutral/secondary
 * - Progress fill: Gradient from blue-300 → blue-500 → blue-700 as progress increases
 * - Position: fixed below FORTUNE logo header (not below nav, which scrolls away)
 * - Forward-only: Progress never decreases when scrolling back up
 *
 * Performance optimizations:
 * - Uses CSS transforms (scaleX) instead of width for no layout shifts
 * - Throttled scroll handler via requestAnimationFrame
 * - Caches DOM element references and bounds (recalculated only on resize)
 * - State updates throttled to 1%+ changes to reduce re-renders
 *
 * Default: progress bar is always visible (no props). A/B test variants (tested against default):
 * - bodyWidth: Constrain to article body width on desktop
 * - appearAfter: Only show after reaching X% scroll progress
 * - mobileOnly: Hide on desktop devices
 */
'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { cn } from '@/app_lib/utils/utils';

/** Maximum percentage value (100%) */
const MAX_PERCENTAGE = 100;

/** Threshold for switching gradient from low to high */
const GRADIENT_THRESHOLD = 50;

/** Minimum percentage change required to trigger a state update */
const MIN_UPDATE_THRESHOLD = 1;

const progressBarVariants = tv({
  defaultVariants: {
    device: 'all',
    visibility: 'visible',
    width: 'full',
  },
  slots: {
    // Transform uses CSS custom property --progress set via style prop
    bar: 'h-full origin-left transition-transform duration-75 ease-out [transform:scaleX(var(--progress))]',
    // Sticky positioning - sticks right below the header when scrolling
    container:
      'sticky w-full h-1 bg-secondary z-body-overlay top-[calc(var(--leaderboard-height)+var(--header-height))] transition-opacity duration-300',
  },
  variants: {
    // Mobile only variant
    device: {
      all: '',
      mobileOnly: 'md:hidden',
    },
    // Gradient based on progress percentage using CSS variables
    // At 0-50%: blue-300 to blue-500
    // At 50-100%: blue-300 → blue-500 → blue-700
    gradient: {
      high: 'bg-[linear-gradient(to_right,var(--color-blue-300),var(--color-blue-500),var(--color-blue-700))]',
      low: 'bg-[linear-gradient(to_right,var(--color-blue-300),var(--color-blue-500))]',
    },
    // Visibility states for delayed appearance
    visibility: {
      hidden: 'opacity-0',
      visible: 'opacity-100',
    },
    // Body width variant: constrain to article width on desktop
    width: {
      body: 'md:max-w-[var(--article-max-width,780px)] md:mx-auto',
      full: '',
    },
  },
});

interface ProgressBarProps {
  className?: string;
  /** Constrain width to article body on desktop */
  bodyWidth?: boolean;
  /** Only show after this scroll percentage (0-100) */
  appearAfter?: number;
  /** Only show on mobile devices */
  mobileOnly?: boolean;
}

interface ArticleBounds {
  top: number;
  end: number;
}

/**
 * Calculate scroll percentage based on viewport position and article bounds
 */
const calculateScrollPercentage = (bounds: ArticleBounds): number | null => {
  const { top: articleTop, end: articleEnd } = bounds;
  const viewportBottom = window.scrollY + window.innerHeight;

  // Don't show progress until article enters viewport
  if (viewportBottom < articleTop) {
    return null;
  }

  const articleHeight = articleEnd - articleTop;
  if (articleHeight <= 0) {
    return null;
  }

  const scrolledInArticle = viewportBottom - articleTop;
  const rawPercentage = (scrolledInArticle / articleHeight) * MAX_PERCENTAGE;
  return Math.max(0, Math.min(rawPercentage, MAX_PERCENTAGE));
};

export const ProgressBar = ({
  className,
  bodyWidth = false,
  appearAfter = 0,
  mobileOnly = false,
}: ProgressBarProps) => {
  const [percentage, setPercentage] = useState<number>(0);
  const [isVisible, setIsVisible] = useState<boolean>(appearAfter === 0);

  // Cached refs for performance - avoid DOM queries on every scroll
  const maxPercentageRef = useRef<number>(0);
  const lastUpdatedPercentageRef = useRef<number>(0);
  const articleRef = useRef<HTMLElement | null>(null);
  const boundsRef = useRef<ArticleBounds | null>(null);

  const { bar, container } = progressBarVariants();

  // Calculate and cache article boundaries
  const calculateBounds = useCallback(() => {
    const article = articleRef.current || document.querySelector('article');
    if (!article) {
      boundsRef.current = null;
      return;
    }

    // Cache the article reference
    articleRef.current = article;

    const authorBio = document.getElementById('author-bio');
    const articleRect = article.getBoundingClientRect();
    const articleTop = articleRect.top + window.scrollY;

    // End point is where author bio starts (end of article content), or article bottom as fallback
    const articleEnd = authorBio
      ? authorBio.getBoundingClientRect().top + window.scrollY
      : articleRect.bottom + window.scrollY;

    boundsRef.current = { end: articleEnd, top: articleTop };
  }, []);

  // Optimized scroll handler - uses cached bounds, no DOM queries
  const handleScrollProgress = useCallback(() => {
    const bounds = boundsRef.current;
    if (!bounds) {
      calculateBounds();
      return;
    }

    const newPercentage = calculateScrollPercentage(bounds);
    if (newPercentage === null) {
      return;
    }

    // Forward-only: only update if new percentage is higher than max reached
    if (newPercentage <= maxPercentageRef.current) {
      return;
    }

    maxPercentageRef.current = newPercentage;

    // Only trigger state update if change exceeds threshold (reduces re-renders)
    const shouldUpdate = newPercentage - lastUpdatedPercentageRef.current >= MIN_UPDATE_THRESHOLD;
    if (!shouldUpdate) {
      return;
    }

    lastUpdatedPercentageRef.current = newPercentage;
    setPercentage(newPercentage);

    // Handle delayed appearance
    if (!isVisible && newPercentage >= appearAfter) {
      setIsVisible(true);
    }
  }, [calculateBounds, appearAfter, isVisible]);

  useEffect(() => {
    // Calculate initial bounds on mount
    calculateBounds();

    // Calculate initial progress
    handleScrollProgress();

    // Set up ResizeObserver to recalculate bounds when layout changes
    const resizeObserver = new ResizeObserver(() => {
      calculateBounds();
    });

    // Observe the article element if found
    if (articleRef.current) {
      resizeObserver.observe(articleRef.current);
    }

    // Also observe document body for general layout changes
    resizeObserver.observe(document.body);

    // Throttle scroll events for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScrollProgress();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', calculateBounds, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', calculateBounds);
      resizeObserver.disconnect();
    };
  }, [handleScrollProgress, calculateBounds]);

  const gradientVariant = percentage <= GRADIENT_THRESHOLD ? 'low' : 'high';
  const progressValue = percentage / MAX_PERCENTAGE;

  return (
    <div
      aria-hidden='true'
      className={cn(
        container(),
        progressBarVariants.variants.width[bodyWidth ? 'body' : 'full'],
        progressBarVariants.variants.visibility[isVisible ? 'visible' : 'hidden'],
        progressBarVariants.variants.device[mobileOnly ? 'mobileOnly' : 'all'],
        className,
      )}
      data-cy='reading-progress-bar'
    >
      <div
        className={cn(bar(), progressBarVariants.variants.gradient[gradientVariant])}
        style={{ '--progress': progressValue } as React.CSSProperties}
      />
    </div>
  );
};
